/**
 * Carga condicional de PostHog basada en consentimiento.
 *
 * - NO inyecta nada hasta que el usuario acepte la categoría "analiticas".
 * - Si revocan después de aceptar, llama a posthog.opt_out_capturing() y elimina cookies.
 * - Se monta una sola vez en App.js; usa subscribe() del módulo de consentimiento.
 */
import { useEffect, useRef } from "react";
import { getConsent, subscribe } from "@/lib/cookieConsent";

const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";

function loadPostHogSnippet() {
  // Si ya está cargado, no hacer nada
  if (typeof window === "undefined" || window.posthog) return;
  // Snippet oficial PostHog v1.x (inyecta script asíncrono)
  // eslint-disable-next-line
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}p=t.createElement("script"),p.type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js";r=t.getElementsByTagName("script")[0];r.parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    session_recording: {
      recordCrossOriginIframes: true,
      capturePerformance: false,
      maskAllInputs: true, // privacidad: no captura contenido tecleado
    },
    // Carga con opt-in explícito por consentimiento
    opt_out_capturing_by_default: false,
  });
}

function unloadPostHog() {
  if (typeof window === "undefined" || !window.posthog) return;
  try {
    window.posthog.opt_out_capturing();
    window.posthog.stopSessionRecording?.();
  } catch {
    // ignorar
  }
  // Borrar cookies de PostHog si existen
  try {
    const cookies = document.cookie.split(";");
    cookies.forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name.startsWith("ph_") || name === "ph_phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs_posthog") {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`;
        document.cookie = `${name}=; Max-Age=0; path=/`;
      }
    });
  } catch {
    // ignorar
  }
}

export default function PostHogLoader() {
  const loadedRef = useRef(false);

  useEffect(() => {
    const apply = (consent) => {
      const allow = !!consent?.analiticas;
      if (allow && !loadedRef.current) {
        loadPostHogSnippet();
        loadedRef.current = true;
      } else if (!allow && loadedRef.current) {
        unloadPostHog();
        loadedRef.current = false;
      } else if (allow && loadedRef.current && window.posthog?.opt_in_capturing) {
        // Ya cargado, asegurar opt-in
        window.posthog.opt_in_capturing();
      }
    };
    // Estado inicial
    apply(getConsent());
    // Suscribir a cambios futuros
    const unsub = subscribe(apply);
    return () => unsub();
  }, []);

  return null;
}
